import { pushRoute } from '../../lib/router.js';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import Empty from '../../components/Empty.jsx';
import {
  loadingContent,
  stakeholdersContent,
} from '../../components/EmptyStates.jsx';
import ProjectHeader from '../../components/headers/PageHeaders/Project/ProjectHeader.jsx';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import Stakeholders from '../../components/projects/Stakeholders/index.jsx';

import StakeholdersActionsDropdown from '../../components/projects/Stakeholders/StakeholdersActionsDropdown.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { retrieveProject, selectProject } from '../../modules/projects.js';
import {
  deleteStakeholder,
  downloadPdfReport,
  resetDraftId,
  retrieveStakeholders,
  searchStakeholders,
  selectSearchRetrieveStakeholdersState,
  selectStakeholders,
  updateSearchQuery,
} from '../../modules/stakeholders.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import {
  retrieveStakeholdersHistory,
  selectStakeholdersHistory,
} from '../../modules/stakeholdersHistory.js';

const mapStateToProps = (state, ownProps) => ({
  stakeholders: selectStakeholders(state, ownProps),
  searchRetrieveState: selectSearchRetrieveStakeholdersState(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  changeHistory: selectStakeholdersHistory(state, ownProps),
  apiUrl: state.config.apiUrl,
});

const mapDispatchToProps = {
  retrieveProject,
  retrieveStakeholders,
  searchStakeholders,
  updateSearchQuery,
  downloadPdfReport,
  deleteStakeholder,
  retrieveStakeholdersHistory,
  resetDraftId,
};

const StakeholdersContainer = (props) => {
  const {
    project,
    projectId,
    retrieveProject,
    searchStakeholders,
    updateSearchQuery,
    stakeholders,
    searchRetrieveState,
    retrieveStakeholdersHistory,
    changeHistory,
    resetDraftId,
    deleteStakeholder,
    apiUrl,
  } = props;

  const {
    query,
    success: searchSuccess,
    pending: searchPending,
  } = searchRetrieveState;

  const searchResults = stakeholders
    ? stakeholders.filter((stakeholder) => !stakeholder.draft)
    : null;

  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [hasEverSearched, setHasEverSearched] = useState(false);

  const csvLoader = useFetchFile();
  const pdfLoader = useFetchFile();
  // different loaders prevent "Loading..." in all places in same time

  const handleSearch = (event) => {
    const query = event.target.value;
    setHasEverSearched(true);
    updateSearchQuery(query);
    searchStakeholders(projectId, query);
  };

  const sortByASC = (arr) =>
    arr.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

  const onDownloadReport = () => {
    const searchParam = query ? `?search=&search=${query}` : '';
    pdfLoader.downloadBlob(
      apiUrl,
      `projects/${projectId}/stakeholders/pdf${searchParam}`,
      null,
      'stakeholders-report',
      'pdf'
    );
  };

  const onDownloadReportStakeholder = (stakeholder) => {
    pdfLoader.downloadBlob(
      apiUrl,
      `stakeholders/${stakeholder.id}/profile_pdf`,
      null,
      `${stakeholder.name}_stakeholder-report`,
      'pdf'
    );
  };

  const onDownloadCsv = () => {
    const searchParam = query ? `?search=&search=${query}` : '';
    csvLoader.downloadBlob(
      apiUrl,
      `projects/${projectId}/stakeholders/csv${searchParam}`,
      null,
      `${project.name} stakeholder-analysis-table`,
      'csv'
    );
  };

  const onAddStakeholder = () => {
    if (project.archived) {
      window.alert(getOrgTermsContent('archivedProjectMessage'));
    } else {
      resetDraftId();
      pushRoute('projects/stakeholders/new/step-stakeholder', { projectId });
    }
  };

  const onEditStakeholder = (stakeholderId) => {
    resetDraftId();
    pushRoute('projects/stakeholders/edit/step-stakeholder', {
      projectId,
      stakeholderId,
    });
  };

  const onDeleteStakeholder = (stakeholderId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      searchStakeholders(projectId, '')
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    retrieveProject(projectId);
    searchStakeholders(projectId, '').then(() => {
      setIsFirstLoading(false);
    });
    retrieveStakeholdersHistory(projectId);
  }, []);

  // Show loading on first mount until data is fetched
  if (isFirstLoading) {
    return <Empty simplified content={loadingContent} />;
  }

  if (
    searchSuccess &&
    !!searchResults &&
    !searchResults.length &&
    !query &&
    !hasEverSearched
  ) {
    return (
      <React.Fragment>
        <ProjectHeader project={project} />
        <SubHeader flexJustify small>
          <div className="flex">
            <h2>Stakeholder Analysis</h2>
            <strong className="header-item bordered text-muted">
              Total:&nbsp;
              <span className="text-primary">{searchResults.length}</span>
            </strong>
          </div>
          <div className="flex flex-1 flex-end">
            {project?.manageProjectEntities ? (
              <div className="flex">
                <AuthorizedAction action="create" subject="stakeholder">
                  <Button
                    bsStyle="success"
                    style={{ marginRight: '18px' }}
                    onClick={onAddStakeholder}
                  >
                    <PlusButtonIcon />
                    Add Stakeholder
                  </Button>
                </AuthorizedAction>
                <AuthorizedAction action="create" subject="stakeholder">
                  <div>
                    <StakeholdersActionsDropdown
                      projectId={project.id}
                      project={project}
                      stakeholders={sortByASC(searchResults)}
                      onDownloadReport={onDownloadReport}
                      isReportLoading={pdfLoader.loading}
                      onDownloadCsv={onDownloadCsv}
                      isCsvLoading={csvLoader.loading}
                    />
                  </div>
                </AuthorizedAction>
              </div>
            ) : null}
          </div>
        </SubHeader>

        <Empty
          content={stakeholdersContent}
          action={onAddStakeholder}
          actionAuthorization={{
            action: project?.manageProjectEntities ? 'create' : null,
            subject: 'stakeholder',
          }}
          addBtn
        ></Empty>
      </React.Fragment>
    );
  }

  return project && searchResults ? (
    <React.Fragment>
      <Stakeholders
        projectId={projectId}
        project={project}
        onAddStakeholder={onAddStakeholder}
        onEditStakeholder={onEditStakeholder}
        stakeholders={sortByASC(searchResults)}
        handleSearch={handleSearch}
        searchSuccess={searchSuccess}
        searchPending={searchPending}
        isFirstLoading={isFirstLoading}
        onDownloadReport={onDownloadReport}
        onDownloadReportStakeholder={onDownloadReportStakeholder}
        changeHistory={changeHistory}
        onDownloadCsv={onDownloadCsv}
        onDeleteStakeholder={onDeleteStakeholder}
        isReportLoading={pdfLoader.loading}
        isCsvLoading={csvLoader.loading}
      />
    </React.Fragment>
  ) : (
    <Empty content={loadingContent} />
  );
};

StakeholdersContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StakeholdersContainer);
